type GraphQLErrorLike = {
  message?: unknown;
  extensions?: Record<string, unknown> | null;
};

export type SelfCheckinErrorAction =
  | "contact_staff"
  | "edit_phone"
  | "edit_email"
  | "edit_reminders"
  | "retry";
export type SelfCheckinErrorContext = "checkin" | "checkout" | "request";

export interface SelfCheckinErrorPresentation {
  action: SelfCheckinErrorAction;
  category:
    | "checkout_acknowledgment"
    | "invalid_reminder_consent"
    | "email_in_use"
    | "generic"
    | "invalid_phone"
    | "invalid_email"
    | "network"
    | "room_unavailable";
  message: string;
}

export const ROOM_UNAVAILABLE_MESSAGE =
  "We can’t complete your check-in because your assigned room is unavailable. Please see Basecamp staff for help.";

export const GENERIC_SELF_CHECKIN_MESSAGE =
  "We couldn’t complete your check-in. Please try again or see Basecamp staff for help.";

export const GENERIC_SELF_CHECKOUT_MESSAGE =
  "We couldn’t complete your checkout. Please try again or see Basecamp staff for help.";

export const GENERIC_SELF_CHECKIN_REQUEST_MESSAGE =
  "We couldn’t prepare your request. Please try again or see Basecamp staff for help.";

export const CHECKOUT_STAFF_ASSIST_MESSAGE =
  "We couldn’t prepare your checkout because your stay needs staff assistance. Please see Basecamp staff for help.";

export const REQUEST_STAFF_ASSIST_MESSAGE =
  "We couldn’t prepare your request because your stay needs staff assistance. Please see Basecamp staff for help.";

export const NETWORK_SELF_CHECKIN_MESSAGE =
  "We couldn’t reach Basecamp. Check your connection and try again.";

export const NO_SELF_CHECKIN_RESERVATION_MESSAGE =
  "No reservation found for this DOD ID.";

export const NO_ACTIVE_SELF_CHECKOUT_RESERVATION_MESSAGE =
  "No active reservation found for this DOD ID.";

export const SELF_CHECKIN_LOOKUP_MESSAGE =
  "We couldn’t look up your reservation. Please try again or see Basecamp staff for help.";

export const SELF_CHECKOUT_LOOKUP_MESSAGE =
  "We couldn’t look up your stay for checkout. Please try again or see Basecamp staff for help.";

export const SELF_CHECKIN_REQUEST_LOOKUP_MESSAGE =
  "We couldn’t look up your stay for this request. Please try again or see Basecamp staff for help.";

export const GENERIC_BASECAMP_REQUEST_MESSAGE =
  "We couldn’t submit your request. Please try again or see Basecamp staff for help.";

export const BASECAMP_REQUEST_PHOTO_MESSAGE =
  "Photo upload failed. Please try again or see Basecamp staff for help.";

const INVALID_EMAIL_MESSAGE =
  "Please enter a valid email address and try again.";

const EMAIL_IN_USE_MESSAGE =
  "That email is already in use. Please enter a different email address.";

export class SelfCheckinLookupNotFoundError extends Error {
  constructor(readonly context: SelfCheckinErrorContext) {
    super(
      context === "checkout"
        ? NO_ACTIVE_SELF_CHECKOUT_RESERVATION_MESSAGE
        : NO_SELF_CHECKIN_RESERVATION_MESSAGE,
    );
    this.name = "SelfCheckinLookupNotFoundError";
  }
}

const getStaffAssistMessage = (context: SelfCheckinErrorContext): string => {
  if (context === "checkout") return CHECKOUT_STAFF_ASSIST_MESSAGE;
  if (context === "request") return REQUEST_STAFF_ASSIST_MESSAGE;
  return ROOM_UNAVAILABLE_MESSAGE;
};

const getGenericMessage = (context: SelfCheckinErrorContext): string => {
  if (context === "checkout") return GENERIC_SELF_CHECKOUT_MESSAGE;
  if (context === "request") return GENERIC_SELF_CHECKIN_REQUEST_MESSAGE;
  return GENERIC_SELF_CHECKIN_MESSAGE;
};

const getLookupMessage = (context: SelfCheckinErrorContext): string => {
  if (context === "checkout") return SELF_CHECKOUT_LOOKUP_MESSAGE;
  if (context === "request") return SELF_CHECKIN_REQUEST_LOOKUP_MESSAGE;
  return SELF_CHECKIN_LOOKUP_MESSAGE;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toGraphQLErrors = (error: unknown): GraphQLErrorLike[] => {
  if (Array.isArray(error)) return error.filter(isRecord);
  if (!isRecord(error)) return [];

  if (Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors.filter(isRecord);
  }

  if (Array.isArray(error.errors)) {
    return error.errors.filter(isRecord);
  }

  if (error.extensions) return [error];
  return [];
};

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TypeError) return true;
  if (!isRecord(error)) return false;
  if (error.networkError) return true;

  const message = typeof error.message === "string" ? error.message : "";
  return /failed to fetch|(?:^|\s)load failed|network (?:error|request failed)/i.test(
    message,
  );
};

const getLegacyMessage = (
  error: unknown,
  graphQLErrors: GraphQLErrorLike[],
): string => {
  const graphqlMessage = graphQLErrors.find(
    (item) => typeof item.message === "string",
  )?.message;
  if (typeof graphqlMessage === "string") return graphqlMessage;
  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }
  return "";
};

/**
 * Converts self-check-in failures into the only messages and recovery actions
 * that may be shown to a guest. Internal GraphQL messages are deliberately not
 * returned; extensions.reason is the public client contract.
 */
export const presentSelfCheckinError = (
  error: unknown,
  context: SelfCheckinErrorContext = "checkin",
): SelfCheckinErrorPresentation => {
  const graphQLErrors = toGraphQLErrors(error);
  const reason = graphQLErrors
    .map((item) => item.extensions?.reason)
    .find((value): value is string => typeof value === "string");

  if (reason === "ROOM_UNAVAILABLE" || reason === "ACTIVE_STAY_CONFLICT") {
    return {
      action: "contact_staff",
      category: "room_unavailable",
      message: getStaffAssistMessage(context),
    };
  }

  if (reason === "INVALID_REMINDER_CONSENT") {
    return {
      action: "edit_reminders",
      category: "invalid_reminder_consent",
      message:
        "Text reminders need a valid US mobile number. Update your contact details or turn off text reminders to continue.",
    };
  }

  if (reason === "INVALID_PHONE") {
    return {
      action: "edit_phone",
      category: "invalid_phone",
      message:
        "Please go back and enter a valid phone number in your contact details.",
    };
  }

  if (reason === "INVALID_EMAIL") {
    return {
      action: "edit_email",
      category: "invalid_email",
      message: INVALID_EMAIL_MESSAGE,
    };
  }

  if (reason === "EMAIL_IN_USE") {
    return {
      action: "edit_email",
      category: "email_in_use",
      message: EMAIL_IN_USE_MESSAGE,
    };
  }

  if (isNetworkError(error)) {
    return {
      action: "retry",
      category: "network",
      message: NETWORK_SELF_CHECKIN_MESSAGE,
    };
  }

  // Backward compatibility for servers that predate extensions.reason. Keep
  // the recovery path, but replace the server text with vetted client copy.
  const legacyMessage = getLegacyMessage(error, graphQLErrors);
  if (/email/i.test(legacyMessage)) {
    if (/already|another|in use|exists|associated/i.test(legacyMessage)) {
      return {
        action: "edit_email",
        category: "email_in_use",
        message: EMAIL_IN_USE_MESSAGE,
      };
    }
    if (/invalid|valid|required|provide|missing/i.test(legacyMessage)) {
      return {
        action: "edit_email",
        category: "invalid_email",
        message: INVALID_EMAIL_MESSAGE,
      };
    }
  }

  return {
    action: "retry",
    category: "generic",
    message: getGenericMessage(context),
  };
};

/**
 * Presents lookup failures without trusting server-provided text. A not-found
 * result is only considered expected when the client received a valid lookup
 * payload with an empty reservation list and raised the typed local error.
 */
export const presentSelfCheckinLookupError = (
  error: unknown,
  context: SelfCheckinErrorContext,
): SelfCheckinErrorPresentation => {
  if (error instanceof SelfCheckinLookupNotFoundError) {
    return {
      action: "retry",
      category: "generic",
      message: error.message,
    };
  }

  if (isNetworkError(error)) {
    return {
      action: "retry",
      category: "network",
      message: NETWORK_SELF_CHECKIN_MESSAGE,
    };
  }

  return {
    action: "retry",
    category: "generic",
    message: getLookupMessage(context),
  };
};

export const presentBasecampRequestError = (
  error: unknown,
  stage: "photo" | "submit" = "submit",
): SelfCheckinErrorPresentation => {
  if (
    toGraphQLErrors(error).some(
      (item) => item.extensions?.code === "TOO_MANY_REQUESTS",
    )
  ) {
    return {
      action: "retry",
      category: "generic",
      message:
        "Too many reports or photo requests. Please wait a few minutes and try again.",
    };
  }
  if (isNetworkError(error)) {
    return {
      action: "retry",
      category: "network",
      message: NETWORK_SELF_CHECKIN_MESSAGE,
    };
  }

  return {
    action: "retry",
    category: "generic",
    message:
      stage === "photo"
        ? BASECAMP_REQUEST_PHOTO_MESSAGE
        : GENERIC_BASECAMP_REQUEST_MESSAGE,
  };
};
